/**
 * Vietnamese message catalog.
 *
 * Typed as Record<MessageKey, string>, so it defines every key in en.ts.
 */
import type { MessageKey } from './en';

export const vi: Record<MessageKey, string> = {
  // Common
  'common.unknown': 'Không xác định',
  'common.tryAgain': 'Thử lại',
  'common.walletFallback': 'Ví',
  'common.loadingWallets': 'Đang tải ví của bạn...',
  'common.back': 'Quay lại',
  'common.close': 'Đóng',
  'common.loading': 'Đang tải…',
  'common.select': 'Chọn',
  'common.selected': 'Đã chọn',
  'common.beta': 'BETA',
  'common.cancel': 'Hủy',
  'common.refresh': 'Làm mới',
  'common.refreshing': 'Đang làm mới',
  'common.all': 'Tất cả',
  'common.wallets': 'ví',
  'common.wallet': 'ví',
  'common.networks': 'mạng',
  'common.network': 'mạng',
  'common.pending': 'Đang chờ',
  'common.unavailable': 'Không khả dụng',
  'common.unverified': 'Chưa xác minh',
  'common.settings': 'Cài đặt',
  'menu.title': 'Menu',
  'menu.open': 'Mở menu',
  'menu.close': 'Đóng menu',
  'common.tier.testnet': 'Testnet',
  'common.tier.mainnet': 'Mainnet',
  'common.theme.auto': 'Tự động',
  'common.theme.light': 'Sáng',
  'common.theme.dark': 'Tối',

  // Navigation
  'nav.primary': 'Chính',
  'nav.home': 'Trang chủ',

  // Settings — page
  'settings.title': 'Cài đặt',

  // Settings — approvals section
  'settings.approvals.title': 'Phê duyệt',
  'settings.approvals.hint': 'Xem lại các giao dịch đang chờ quyết định của bạn trước khi cổng ký.',
  'settings.approvals.link': 'Xem các phê duyệt đang chờ',

  // Settings — appearance section
  'settings.appearance.title': 'Giao diện',
  'settings.appearance.hint': 'Chọn cách hiển thị ứng dụng.',
  'settings.appearance.themeGroup': 'Chủ đề',

  // Settings — language section
  'preauth.menu.title': 'Ngôn ngữ và giao diện',
  'preauth.menu.open': 'Mở menu ngôn ngữ và giao diện',
  'settings.language.title': 'Ngôn ngữ',
  'settings.language.hint': 'Chọn ngôn ngữ của bạn.',

  // Settings — developer mode section
  'settings.devmode.title': 'Chế độ nhà phát triển',
  'settings.devmode.label': 'Chế độ nhà phát triển',
  'settings.devmode.desc': 'Bật để dùng testnet. Tắt để dùng mainnet.',
  'settings.devmode.note': 'Chế độ testnet. Số dư không có giá trị thực.',

  // Settings — deployment section
  'settings.deployment.title': 'Triển khai',
  'settings.deployment.network': 'Mạng',
  'settings.deployment.gatewayEnv': 'Môi trường cổng',
  'settings.deployment.bitcoinNetwork': 'Mạng Bitcoin',
  'settings.deployment.organization': 'Tổ chức',

  // Settings — security section (device passkeys)
  'settings.security.title': 'Bảo mật',
  'settings.security.hint': 'Thêm khóa truy cập thiết bị để phê duyệt giao dịch trên thiết bị này trước khi cổng ký.',
  'settings.security.methodsTitle': 'Phương thức xác thực',
  'settings.security.addDevice': 'Thêm phương thức xác thực',
  'settings.security.limitReached': 'Chỉ có thể đăng ký tối đa {max} phương thức xác thực.',
  'settings.security.adding': 'Đang chờ thiết bị…',
  'settings.security.remove': 'Xóa',
  'settings.security.removing': 'Đang xóa…',
  'settings.security.empty': 'Chưa đăng ký khóa truy cập thiết bị nào.',
  'settings.security.loading': 'Đang tải phương thức xác thực…',
  'settings.security.retry': 'Thử lại',
  'settings.security.unsupported': 'Trình duyệt này không hỗ trợ khóa truy cập thiết bị.',
  'settings.security.enrolledOn': 'Đã thêm {date}',
  'settings.security.lastUsed': 'Dùng gần nhất {date}',
  'settings.security.deviceFallbackLabel': 'Khóa truy cập thiết bị',

  // Create-a-passkey prompt (one-time, after a fresh OAuth or OTP login)
  'passkey.create.title': 'Tạo khóa truy cập',
  'passkey.create.body': 'Đăng nhập bằng vân tay, khuôn mặt hoặc khóa màn hình. Không cần nhớ mật khẩu.',
  'passkey.create.cta': 'Tạo khóa truy cập',
  'passkey.create.creating': 'Đang chờ thiết bị của bạn…',
  'passkey.create.dismiss': 'Không hiển thị lại',
  'passkey.create.error': 'Không thể tạo khóa truy cập. Hãy thử lại hoặc thêm sau trong mục Bảo mật của menu.',
  'passkey.stateUnknown':
    'Giao dịch chuyển không được thực hiện. Không thể xác nhận các phương thức xác thực của bạn do lỗi kết nối; hãy thử lại.',

  // Settings — account section
  'settings.account.title': 'Tài khoản',
  'settings.account.signout': 'Đăng xuất',

  // Login — page
  'login.title': 'Tiếp tục đến ví của bạn',
  'login.eyebrow': 'Xin chào',
  'login.error.start': 'Không thể bắt đầu đăng nhập. Hãy kiểm tra kết nối và thử lại.',
  'login.divider': 'hoặc',
  'login.more': 'Các cách đăng nhập khác',

  // Login — providers ({name} is the brand for the fallback label)
  'login.provider.google': 'Tiếp tục với Google',
  'login.provider.apple': 'Tiếp tục với Apple',
  'login.provider.discord': 'Tiếp tục với Discord',
  'login.provider.twitter': 'Tiếp tục với X',
  'login.provider.kakao': 'Tiếp tục với Kakao',
  'login.provider.fallback': 'Tiếp tục với {name}',
  'login.provider.unavailable': 'Phương thức đăng nhập này hiện không khả dụng.',

  // Login — passkey sign-in
  'login.passkey.button': 'Đăng nhập an toàn',
  'login.passkey.desc': 'Sử dụng phương thức xác thực đã đăng ký trên thiết bị của bạn, chẳng hạn như xác thực khuôn mặt hoặc vân tay.',
  'login.passkey.pending': 'Đang chờ khóa truy cập…',
  'login.passkey.cancelled':
    'Đăng nhập đã bị hủy hoặc thiết bị này không có khóa truy cập cho trang web này. Hãy thử lại hoặc chọn phương thức khác.',
  'login.passkey.error': 'Không thể đăng nhập bằng khóa truy cập. Hãy thử lại hoặc chọn phương thức khác.',
  // Shown when sign-in found no passkey or no account. Passkey creation lives in the
  // low-weight "create an account with a passkey" link below, so this points there.
  'login.passkey.noAccount': 'Không tìm thấy khóa truy cập trên thiết bị này. Hãy dùng mục “Tạo ví” bên dưới.',

  // Login — passkey signup (a low-weight link below sign-in for a new user)
  'login.signup.prompt': 'Bạn mới đến?',
  'login.signup.link': 'Tạo ví',
  'login.signup.pending': 'Đang tạo tài khoản…',
  'login.signup.exists': 'Bạn đã có tài khoản. Hãy đăng nhập bằng khóa truy cập.',
  'login.signup.error': 'Không thể tạo tài khoản. Hãy thử lại hoặc chọn phương thức khác.',

  // Login — organization escape hatch (inside the "more ways to sign in" section)
  'login.org.label': 'ID tổ chức',
  'login.org.note': 'Để trống để đăng nhập vào không gian riêng của bạn, rồi chọn phương thức ở trên.',

  // Login — developer fallback
  'login.dev.body': 'Khi phát triển cục bộ với cổng, hãy đặt token từ bảng điều khiển của trình duyệt:',

  // Dashboard (home)
  'dashboard.offline': 'Có vẻ bạn đang ngoại tuyến. Hãy kết nối lại để tải ví.',
  'dashboard.empty': 'Chưa có ví. Hãy tạo một ví để bắt đầu.',

  // Home components
  'home.account.allWallets': 'Tất cả ví',
  'home.account.subtitle': '{wallets} {walletWord} · {networks} {networkWord}',
  'home.switcher.aria': 'Chuyển tài khoản, hiện tại là {name}',
  'home.copy.aria': 'Sao chép địa chỉ {name}',
  'home.portfolio.aria': 'Số dư danh mục',
  'home.portfolio.total': 'Tổng số dư',
  'home.actions.aria': 'Thao tác ví',
  'home.action.swapUnavailable': 'Tính năng hoán đổi chưa khả dụng.',
  'home.action.buySellUnavailable': 'Tính năng mua bán chưa khả dụng.',
  'home.action.buySell': 'Mua/Bán',
  'home.network.testnetTitle': 'Môi trường testnet. Số dư hiển thị ở đây không có giá trị thực.',
  'home.network.mainnetTitle': 'Mainnet. Số dư phản ánh mạng đang hoạt động.',

  // App navigation / account-wide surfaces
  'activity.title': 'Hoạt động',
  'activity.history': 'Lịch sử',

  // Transaction list
  'tx.aria.history': 'Lịch sử giao dịch',
  'tx.date.pending': 'Đang chờ',
  'tx.direction.receive': 'Nhận',
  'tx.direction.send': 'Gửi',
  'tx.direction.swap': 'Hoán đổi',
  'tx.relation.from': 'Từ',
  'tx.relation.to': 'Đến',
  'tx.swapDetails.aria': 'Chi tiết hoán đổi: từ {from} sang {to}',
  'tx.status.pending': 'Đang chờ',
  'tx.status.pending_approval': 'Chờ phê duyệt',
  'tx.status.pending_policy': 'Đang kiểm tra chính sách',
  'tx.status.signing': 'Đang ký',
  'tx.status.signed': 'Đã ký',
  'tx.status.submitted': 'Đã gửi',
  'tx.status.broadcast': 'Đã phát lên mạng',
  'tx.status.confirmed': 'Đã xác nhận',
  'tx.status.failed': 'Thất bại',
  'tx.status.rejected': 'Đã từ chối',
  'tx.status.unknown': 'Không xác định',
  'tx.loading': 'Đang tải hoạt động…',
  'tx.empty': 'Chưa có hoạt động.',
  'tx.empty.hint': 'Các giao dịch gửi và hoán đổi sẽ xuất hiện ở đây.',
  'tx.loadingMore': 'Đang tải thêm…',
  'tx.explorer.aria': 'Xem trên trình khám phá khối',

  // Approvals
  'approvals.title': 'Phê duyệt',
  'approvals.error.load': 'Không thể tải các phê duyệt',
  'approvals.empty.title': 'Bạn đã xử lý xong',
  'approvals.empty.body': 'Không có giao dịch nào đang chờ quyết định. Yêu cầu mới cần bạn xem lại sẽ xuất hiện ở đây.',
  'approvals.wallet.title': 'Phê duyệt của ví này',
  'approvals.wallet.resolving': 'Đang kiểm tra các phê duyệt thuộc ví này…',
  'approvals.wallet.empty.title': 'Ví này không có mục đang chờ',
  'approvals.wallet.empty.body': 'Không có giao dịch nào của ví này đang chờ quyết định. Yêu cầu cần bạn xem lại sẽ xuất hiện ở đây.',
  'approvals.status.pending': 'Đang chờ',
  'approvals.status.approved': 'Đã phê duyệt',
  'approvals.status.rejected': 'Đã từ chối',
  'approvals.status.expired': 'Đã hết hạn',
  'approvals.quorum': 'Đã có {received}/{required} phê duyệt',
  'approvals.quorum.aria': 'Tiến độ đủ số lượng phê duyệt',
  'approvals.waiting.more': 'Đang chờ thêm {count} {word}',
  'approvals.waiting.approval': 'phê duyệt',
  'approvals.waiting.approvals': 'phê duyệt',
  'approvals.waiting.signing': 'Đang chờ quy trình ký hoàn tất',
  'approvals.timeline.checker': 'Người kiểm tra',
  'approvals.timeline.acted': 'đã xử lý',
  'approvals.detail.loading': 'Đang tải chính xác giao dịch…',
  'approvals.detail.unavailable': 'Không thể tải chi tiết giao dịch. Chỉ phê duyệt hoặc từ chối nếu bạn có thể xác nhận giao dịch này bằng cách khác.',
  'approvals.detail.notProvided': 'Không có trong bản ghi giao dịch',
  'approvals.detail.requestedBy': 'Người yêu cầu',
  'approvals.detail.transactionStatus': 'Trạng thái giao dịch',
  'approvals.summary.transaction': 'Giao dịch',
  'approvals.action.busy': 'Đang phối hợp chữ ký đa bên. Khi đủ số lượng phê duyệt, cổng sẽ ký và phát giao dịch trước khi phản hồi; quá trình này có thể mất vài giây.',
  'approvals.action.approve': 'Phê duyệt',
  'approvals.action.reject': 'Từ chối',
  'approvals.confirm.approveTitle': 'Phê duyệt giao dịch này?',
  'approvals.confirm.rejectTitle': 'Từ chối giao dịch này?',
  'approvals.confirm.approveConsequence':
    'Phê duyệt của bạn sẽ được chuyển đến cổng. Khi đủ số lượng phê duyệt, cổng sẽ ký và phát giao dịch trước khi phản hồi; quá trình này có thể mất vài giây. Đừng đóng ứng dụng cho đến khi hoàn tất.',
  'approvals.confirm.rejectConsequence': 'Thao tác này sẽ ngăn giao dịch được ký. Không thể đạt đủ số lượng phê duyệt nên giao dịch sẽ không được phát lên mạng.',
  'approvals.confirm.reasonLabel': 'Lý do (không bắt buộc)',
  'approvals.confirm.reasonPlaceholder': 'Thêm ghi chú cho nhật ký kiểm toán',
  'approvals.confirm.approving': 'Đang phê duyệt',
  'approvals.confirm.rejecting': 'Đang từ chối',
  'approvals.section.pending': 'Cần bạn xem lại ({count})',
  'approvals.section.decided': 'Đã quyết định',

  // Create wallet
  'create.title': 'Tạo ví',
  'create.lead': 'Chọn loại ví và tên ví. Việc tạo khóa diễn ra an toàn trên máy chủ; thiết bị của bạn không giữ khóa và không ký tại đây.',
  'create.walletName': 'Tên ví',
  'create.walletPlaceholder': 'Ví của tôi',
  'create.walletType': 'Loại ví',
  'create.kind.evm.label': 'Tài khoản EVM',
  'create.kind.evm.desc': 'Ethereum · Base · BSC · HyperEVM',
  'create.kind.tron.label': 'Tron',
  'create.kind.tron.desc': 'TRX và token TRC-20',
  'create.kind.solana.label': 'Solana',
  'create.kind.solana.desc': 'SOL và token SPL',
  'create.kind.sui.label': 'Sui',
  'create.kind.sui.desc': 'SUI và token Sui',
  'create.kind.bitcoin.label': 'Bitcoin',
  'create.kind.bitcoin.desc': 'BTC · địa chỉ SegWit',

  'provisioning.title': 'Đang tạo các ví của bạn',
  'provisioning.unavailable': 'Không khả dụng tại đây',
  'walletSettings.statusFrozen': 'Bị đóng băng',
  'dashboard.stillCreating': 'Còn {count} ví đang được tạo.',
  'dashboard.createWallet': 'Tạo ví',
  'provisioning.failed': 'Không thể tự động tạo ví của bạn.',
  'provisioning.createManually': 'Tạo ví thủ công',
  'provisioning.succeeded': 'Các ví của bạn đã sẵn sàng.',
  'provisioning.partial':
    'Đã tạo thành công ví cho {created}, nhưng không thể tạo ví cho {missing}.',
  'provisioning.partialHint': 'Bạn có thể thêm các ví còn thiếu theo cách thủ công sau này.',
  'provisioning.pending': 'Các ví của bạn vẫn đang được tạo.',
  'provisioning.pendingHint':
    'Quá trình này chỉ mất giây lát; các ví sẽ tự động xuất hiện trên màn hình chính.',
  'provisioning.goHome': 'Tiếp tục',
  'create.busy': 'Đang chạy quy trình tạo khóa',
  'create.toast.created': 'Đã tạo ví.',

  // OAuth callback
  'oauth.pending.title': 'Đang đăng nhập',
  'oauth.pending.message': 'Đang hoàn tất đăng nhập, vui lòng chờ.',
  'oauth.failure.title': 'Đăng nhập chưa hoàn tất',
  'oauth.failure.fallback': 'Không thể hoàn tất đăng nhập.',
  'oauth.backToSignIn': 'Quay lại đăng nhập',

  // Receive ({chain} is the resolved chain name)
  'receive.title': 'Nhận',
  'receive.subtitle': 'Chọn mạng và địa chỉ để nhận',
  'receive.chain.fallback': 'Ví này',
  'receive.chainSelector.aria': 'Chọn mạng để nhận',
  'receive.addressPicker.label': 'Địa chỉ',
  'receive.addressPending': 'Địa chỉ đang chờ',
  'receive.qr.title': 'Mã QR cho địa chỉ {chain}',
  'receive.qr.unavailable': 'Mã QR không khả dụng. Hãy sao chép địa chỉ bên dưới.',
  'receive.addressLabel': 'Địa chỉ {chain}',
  'receive.warning': 'Chỉ gửi tài sản {chain} đến địa chỉ này.',
  'receive.copy.copy': 'Sao chép địa chỉ',
  'receive.copy.copied': 'Đã sao chép địa chỉ',
  'receive.copy.failed': 'Sao chép thất bại',
  'receive.copy.aria': 'Sao chép địa chỉ vào bảng nhớ tạm',
  'receive.pending.setup': 'Ví này vẫn đang được thiết lập. Địa chỉ sẽ xuất hiện khi ví sẵn sàng.',
  'receive.pending.loading': 'Đang tải địa chỉ của ví này...',
  'receive.empty': 'Chưa có ví nào để nhận tài sản.',

  // Send ({asset}, {chain}, {label}, {reason} are interpolated at render)
  'send.title': 'Gửi',
  'send.selectAsset': 'Chọn tài sản để gửi',
  'send.loadingAssets': 'Đang tải tài sản của bạn...',
  'send.emptyAssets': 'Chưa có tài sản nào có số dư khả dụng. Hãy nhận tài sản trước khi gửi.',
  'send.emptyNoWallets': 'Chưa có ví. Hãy tạo ví để gửi tài sản.',
  'send.backToAssets': 'Quay lại tài sản',
  'send.stepTwo.title': 'Gửi {asset} trên mạng {chain}',
  'send.context.none': 'Chưa chọn ví.',
  'send.context.from': 'Từ {label} trên {chain}',
  'send.context.fromUnsupported': 'Từ {label}. {reason}',
  'send.error.wrongTier': 'Ví này không thuộc mạng đang hoạt động. Hãy chuyển mạng hoặc chọn ví trên mạng hiện tại trước khi gửi.',
  'send.error.noWallet': 'Chọn ví dùng để gửi.',
  'send.error.chainUnavailable': 'Không thể gửi trên mạng này.',
  'send.status.submitting': 'Đang gửi giao dịch.',
  'send.status.buildingPayload': 'Đang đọc phí mạng và nonce.',
  'send.status.pendingPolicy': 'Giao dịch chuyển này cần thêm phê duyệt hoặc đang bị khóa thời gian theo chính sách. Giao dịch chỉ tiếp tục khi đáp ứng điều kiện.',
  'send.status.noApproval': 'Giao dịch đã được tạo nhưng không thể xử lý phê duyệt tại đây. Hãy xem trong màn hình Phê duyệt để tiếp tục.',
  'send.confirm.title': 'Xác nhận gửi',
  'send.confirm.note': 'Hãy kiểm tra kỹ địa chỉ đích. Tài sản sẽ được chuyển và không thể hoàn tác.',
  'send.confirm.to': 'Đến',
  'send.confirm.amount': 'Số tiền',
  'send.confirm.network': 'Mạng',
  'send.confirm.networkFee': 'Phí mạng',
  'send.confirm.networkFeeValue': 'Được cổng ước tính và áp dụng khi tạo giao dịch.',
  'send.confirm.networkFeeValueEvm': 'Được đọc từ mạng khi bạn xác nhận, rồi áp dụng cho giao dịch.',
  'send.confirm.submit': 'Xác nhận và gửi',
  'send.confirm.sending': 'Đang gửi',
  'send.status.title': 'Trạng thái gửi',
  'send.status.statusLabel': 'Trạng thái',
  'send.status.txId': 'ID giao dịch',
  'send.status.hash': 'Hash',
  'send.status.explorer': 'Xem trên trình khám phá khối',
  'send.status.goToApprovals': 'Đi đến Phê duyệt',
  'send.status.backToSend': 'Quay lại Gửi',
  'send.result.status': 'Giao dịch chuyển {status}.',
  'send.result.statusReason': 'Giao dịch chuyển {status}{reason}',

  // Send form and asset picker
  'send.form.recipient': 'Địa chỉ người nhận',
  'send.form.destinationPlaceholder': 'Địa chỉ đích',
  'send.form.noWallet': 'Chọn ví dùng để gửi.',
  'send.form.chainUnavailable': 'Không thể gửi trên mạng này tại đây.',
  'send.validation.recipientRequired': 'Nhập địa chỉ người nhận.',
  'send.validation.evmWebUnavailable': 'Không thể gửi trên mạng này bằng ứng dụng web.',
  'send.validation.evmNoNetwork': 'Hãy chọn đồng coin muốn gửi để xác định mạng. Tài khoản này dùng chung một địa chỉ trên mọi mạng EVM.',
  'send.validation.evmTokenUnsupported': 'Ứng dụng web chưa hỗ trợ gửi token trên mạng EVM. Hãy dùng ứng dụng di động để chuyển token, hoặc gửi đồng coin gốc của mạng tại đây.',
  'send.validation.evmImportedTokenUnsupported': 'Token đã nhập không thể gửi từ ứng dụng web. Chỉ có thể gửi token đã được sổ đăng ký xác minh và coin gốc của mạng tại đây.',
  'send.validation.evmTokenDecimalsUnknown': 'Không thể xác định độ chính xác của token này, nên không thể mã hóa giao dịch một cách an toàn. Chưa ký gì cả.',
  'send.validation.evmPrefix': 'Địa chỉ EVM bắt đầu bằng 0x.',
  'send.validation.evmFormat': 'Địa chỉ EVM là 0x theo sau bởi 40 ký tự hex.',
  'send.validation.evmChecksum': 'Địa chỉ EVM này có checksum không hợp lệ.',
  'send.validation.evmZeroAddress': 'Đây là địa chỉ 0; tài sản gửi tới đó không thể lấy lại.',
  'send.validation.suiPrefix': 'Địa chỉ Sui bắt đầu bằng 0x.',
  'send.validation.suiFormat': 'Địa chỉ Sui gồm 0x và 64 ký tự thập lục phân viết thường theo sau.',
  'send.validation.tronPrefix': 'Địa chỉ Tron bắt đầu bằng T.',
  'send.validation.tronLength': 'Địa chỉ Tron có 34 ký tự.',
  'send.validation.tronChecksum': 'Địa chỉ Tron này có checksum không hợp lệ.',
  'send.validation.tronInvalid': 'Đây không phải địa chỉ Tron hợp lệ.',
  'send.validation.solanaInvalid': 'Địa chỉ này có vẻ không phải địa chỉ Solana.',
  'send.validation.solanaBytes': 'Địa chỉ Solana sau khi giải mã phải có 32 byte.',
  'send.validation.bitcoinChecksum': 'Địa chỉ Bitcoin này có checksum không hợp lệ.',
  'send.validation.bitcoinInvalid': 'Đây không phải địa chỉ Bitcoin hợp lệ.',
  'send.validation.bitcoinSegwitInvalid': 'Đây không phải địa chỉ SegWit hợp lệ.',
  'send.validation.bitcoinTaprootInvalid': 'Đây không phải địa chỉ Taproot hợp lệ.',
  'send.validation.bitcoinWrongNetwork': 'Địa chỉ này thuộc một mạng Bitcoin khác (cần {expected}).',
  'send.validation.bitcoinDust': 'Số tiền thấp hơn giới hạn dust của Bitcoin là {sats} sats.',
  'send.form.amountRequired': 'Nhập số tiền.',
  'send.form.numberRequired': 'Nhập một số.',
  'send.form.amountPositive': 'Số tiền phải lớn hơn 0.',
  'send.form.decimals': '{symbol} hỗ trợ tối đa {decimals} chữ số thập phân.',
  'send.form.exceedsBalance': 'Số tiền vượt quá số dư {symbol} của bạn.',
  'send.form.assetAria': 'Tài sản',
  'send.form.noAssets': 'Không có tài sản',
  'send.form.available': 'Khả dụng: {amount} {symbol}',
  'send.form.review': 'Xem lại giao dịch gửi',
  'send.form.clear': 'Xóa',
  'send.assets.searchLabel': 'Tìm tài sản',
  'send.assets.searchPlaceholder': 'Tìm theo tên hoặc ký hiệu',
  'send.assets.empty': 'Không có tài sản phù hợp. Xóa tìm kiếm hoặc bộ lọc để xem tài sản đang nắm giữ.',

  // Swap ({decimals}, {provider}, {count} are interpolated at render)
  'swap.title': 'Hoán đổi',
  'swap.subtitle': 'Lộ trình tốt nhất từ mọi nhà cung cấp',
  'swap.balance': 'Số dư {amount} {symbol}',
  'swap.max': 'Tối đa',
  'swap.signer.overridden': '{selected} không thể hoán đổi nên đang dùng {label} · {address}.',
  'swap.empty.evm': 'Cần ví có địa chỉ EVM để hoán đổi. Hãy tạo ví từ màn hình chính trước.',
  'swap.error.loadChains': 'không thể tải các mạng',
  'swap.error.quote': 'không thể lấy báo giá',
  'swap.error.executeNotEnabled': 'Dịch vụ này chưa bật tính năng thực hiện hoán đổi (trình ký phát triển đang tắt).',
  'swap.error.executeFallback': 'hoán đổi thất bại',
  'swap.side.from': 'Bạn trả',
  'swap.side.to': 'Bạn nhận',
  'swap.chain.fromAria': 'Mạng nguồn',
  'swap.chain.toAria': 'Mạng đích',
  'swap.amount.fromAria': 'Số tiền gửi',
  'swap.amount.toAria': 'Số tiền nhận',
  'swap.amount.invalidHint': 'Nhập số tiền dương với tối đa {decimals} chữ số thập phân.',
  'swap.flip.aria': 'Đổi chiều hoán đổi',
  'swap.sameTokenHint': 'Chọn hai token khác nhau để hoán đổi.',
  'swap.warning.insufficientBalance': 'Số dư của bạn thấp hơn số tiền này nên giao dịch hoán đổi sẽ thất bại.',
  'swap.warning.insufficientAllowance': 'Cần cấp quyền cho token trước khi hoán đổi.',
  'swap.toast.submitted': 'Đã gửi giao dịch hoán đổi qua {provider}. Theo dõi trong Hoạt động.',
  'swap.confirm.swapping': 'Đang hoán đổi…',
  'swap.confirm.via': 'Hoán đổi qua {provider}',
  'swap.confirm.summary': 'Hoán đổi {inAmount} {inSymbol} lấy khoảng {outAmount} {outSymbol} qua {provider}. Việc quyết toán hoàn tất bất đồng bộ trên mạng đích.',
  'swap.mpcNote': 'Được ký bởi ví của bạn thông qua luồng ký MPC; giao dịch gốc được phát trên chuỗi và nhà cung cấp quyết toán phần khớp lệnh.',
  'swap.confirm.default': 'Hoán đổi',
  'swap.confirm.disabledTitle': 'Tính năng thực hiện hoán đổi sẽ đi kèm tích hợp ký qua cổng',
  'swap.mock.enabledHint': 'Được ký bởi ví phát triển (MPC mô phỏng) · hoán đổi on-chain thực',
  'swap.mock.disabledHint': 'Báo giá trực tiếp · tính năng hoán đổi sẽ đi kèm quy trình ký qua cổng',
  'swap.picker.tokenAria': 'Chọn token',
  'swap.picker.chainAria': 'Chọn mạng',
  'swap.picker.searchToken': 'Tìm tên, ký hiệu hoặc địa chỉ',
  'swap.picker.searchChain': 'Tìm ví',
  'swap.picker.loadingTokens': 'Đang tải token…',
  'swap.picker.noTokens': 'Không có token phù hợp.',
  'swap.picker.noChains': 'Không có mạng phù hợp.',
  'swap.picker.noWallets': 'Không có ví nào nhận được trên chuỗi khả dụng.',
  'swap.picker.routes': '{count} lộ trình',
  'swap.token.aria': 'Chọn token',
  'swap.quote.noRoute': 'Không có lộ trình',
  'swap.quote.best': 'Tốt nhất',
  'swap.quote.networkCost': 'Chi phí mạng',
  'swap.quote.platformFee': 'Phí nền tảng',
  'swap.quote.priceImpact': 'Tác động giá',
  'swap.quote.time': 'Thời gian',

  // Swap detail sheet
  'swap.detail.aria': 'Chi tiết hoán đổi',
  'swap.detail.closeAria': 'Đóng chi tiết',
  'swap.detail.status': 'Trạng thái',
  'swap.detail.paid': 'Bạn đã trả',
  'swap.detail.received': 'Bạn đã nhận',
  'swap.detail.rate': 'Tỷ giá',
  'swap.detail.route': 'Lộ trình',
  'swap.detail.provider': 'Nhà cung cấp',
  'swap.detail.feeQuoted': 'Phí (báo giá)',
  'swap.detail.feeCharged': 'Phí (đã tính)',
  'swap.detail.sourceTx': 'Giao dịch nguồn',
  'swap.detail.destinationTx': 'Giao dịch đích',
  'swap.origin.unreported': 'Đang gửi giao dịch — chưa thể xác định kết quả.',
  'swap.origin.reported': 'Đang kiểm tra mạng để tìm giao dịch của bạn.',
  'swap.origin.verified': 'Giao dịch đã được xác nhận trên chuỗi nguồn — đang chờ chuỗi đích.',
  'swap.origin.reverted': 'Giao dịch đã được thực thi trên chuỗi nhưng thất bại. Không có khoản tiền nào được chuyển.',
  'swap.origin.unknown': 'Không thể xác nhận giao dịch này. Hãy kiểm tra lịch sử ví trước khi thử lại.',
  'swap.origin.noRetry':
    'Không thể thử lại cho đến khi giao dịch được xác nhận hoặc được xác định là không xảy ra.',
  'swap.origin.ackPrompt':
    'Giao dịch hoán đổi này chưa được xác nhận. Hãy kiểm tra lịch sử ví trước khi gỡ bỏ khóa giữ.',
  'swap.origin.ackRelease': 'Tôi đã kiểm tra lịch sử ví — gỡ bỏ khóa giữ này',
  'swap.origin.trackingFailed':
    'Không thể ghi nhận giao dịch hoán đổi này nên giao dịch không được bắt đầu. Không có gì được ký và không có khoản tiền nào được chuyển.',

  // Earn (lending market; {symbol}, {amount}, {usd}, {digest}, {reason} interpolated)
  'earn.title': 'Cho vay & vay',
  'earn.subtitle': 'Dùng tài sản hiện có làm thế chấp để vay vốn, hoặc cho vay để nhận lợi suất và tăng hiệu quả đầu tư.',
  'earn.error.market': 'Thị trường không khả dụng: {message}',
  'earn.error.retry': 'Thử lại',
  'earn.loading': 'Đang tải thị trường cho vay…',
  'earn.empty': 'Hiện không có thị trường cho vay nào.',
  'earn.table.asset': 'Tài sản',
  'earn.table.totalSupply': 'Tổng tiền gửi',
  'earn.table.totalBorrow': 'Tổng vay',
  'earn.table.ltv': 'LTV',
  'earn.table.aria': 'Thị trường cho vay',
  'earn.vault.title': 'Kho {symbol}',
  'earn.vault.supplyYield': 'Lợi suất tiền gửi',
  'earn.vault.borrowInterest': 'Lãi suất vay',
  'earn.vault.marketPrice': 'Giá thị trường',
  'earn.vault.utilization': 'Tỷ lệ sử dụng',
  'earn.vault.depositThreshold': 'Ngưỡng gửi',
  'earn.vault.borrowThreshold': 'Ngưỡng vay',
  'earn.vault.borrowFee': 'Phí vay: {pct}%',
  'earn.account.title': 'Tài khoản của bạn',
  'earn.account.deposits': 'Khoản gửi của bạn',
  'earn.account.borrows': 'Khoản vay của bạn',
  'earn.account.borrowLimit': 'Hạn mức vay',
  'earn.account.liqThreshold': 'Ngưỡng thanh lý',
  'earn.account.health': 'Sức khỏe tài khoản',
  'earn.account.depositedRow': 'Đã gửi {amount} {symbol} (${usd})',
  'earn.account.borrowedRow': 'Đã vay {amount} {symbol} (${usd})',
  'earn.action.supply': 'Gửi vào',
  'earn.action.borrow': 'Vay',
  'earn.action.withdraw': 'Rút',
  'earn.action.repay': 'Trả nợ',
  'earn.actions.aria': 'Hành động cho vay',
  'earn.amount.label': 'Số lượng ({symbol})',
  'earn.amount.available': 'khả dụng ~{amount}',
  'earn.amount.max': 'Tối đa',
  'earn.amount.allDebt': 'Toàn bộ khoản nợ',
  'earn.submit.submitting': 'Đang gửi…',
  'earn.note.sameAssetBorrow': 'Bạn không thể vay {symbol} vì đây là tài sản thế chấp. Hãy chọn tài sản khác hoặc rút khoản gửi trước.',
  'earn.note.overMax.withdraw': 'Chỉ có thể rút khoảng {amount} {symbol}; phần còn lại phải được giữ làm tài sản thế chấp cho khoản vay chưa trả. Hãy trả nợ trước để rút toàn bộ.',
  'earn.note.overMax.borrow': 'Tài sản thế chấp cho phép bạn vay tối đa khoảng {amount} {symbol}.',
  'earn.note.overMax.repay': 'Bạn chỉ nợ khoảng {amount} {symbol}.',
  'earn.note.noSigner': 'Không thể thực hiện: việc ký diễn ra qua cổng, nhưng cả tuyến cho vay của cổng và trình ký phát triển cục bộ đều không thể kết nối.',
  'earn.note.devSigner': 'Trình ký phát triển (testnet): {address}',
  'earn.toast.confirmed': 'Đã xác nhận {action} ({digest}…)',
  'earn.toast.failedReason': '{action} thất bại: {reason}',
  'earn.toast.failedOnChain': '{action} thất bại: lỗi on-chain ({digest}…)',
  'earn.activity.title': 'Hoạt động gần đây',

  // Filters and lists
  'filter.network.aria': 'Bộ lọc mạng',
  'filter.chain.aria': 'Bộ lọc blockchain',
  'filter.chain.selectNetwork': 'Chọn mạng',
  'holdings.empty': 'Chưa có tài sản ở đây. Hãy nhận tài sản hoặc đổi bộ lọc để xem thêm.',
  'holdings.showHidden': 'Hiện {count} mục ẩn · dưới $0.01',
  'holdings.hideSmall': 'Ẩn {count} {word} nhỏ',
  'holdings.balance': 'số dư',
  'holdings.balances': 'số dư',
  'chainSummary.empty': 'Chưa có tài sản ở đây. Hãy nhận tài sản để xem các mạng.',
  'chainSummary.viewAria': 'Xem {name}, {count} {word}',
  'walletChain.empty': 'Chưa có ví trên mạng này.',
  'walletChain.openAria': 'Mở {label}',

  // Account and wallet management sheets
  'account.closeSwitcher': 'Đóng trình chuyển ví',
  'account.title': 'Ví của bạn',
  'account.allDesc': 'tất cả trong một danh sách',
  'account.newWallet': 'Ví mới',
  'account.searchPlaceholder': 'Tìm theo tên hoặc địa chỉ',
  'account.searchAria': 'Tìm ví theo tên hoặc địa chỉ',
  'account.networks': 'Mạng',
  'account.empty.filtered': 'Không có ví nào khớp với bộ lọc.',
  'account.empty.none': 'Chưa có ví.',
  'account.selectAria': 'Chọn {label}, {value}',
  'account.copyAddressAria': 'Sao chép địa chỉ {chain}',
  'account.showQrAria': 'Hiện mã QR nhận trên {chain}',
  'selectNetwork.closeAria': 'Đóng trình chọn mạng',
  'selectNetwork.title': 'Chọn mạng',
  'selectNetwork.searchPlaceholder': 'Tìm mạng',
  'selectNetwork.searchAria': 'Tìm mạng theo tên',
  'selectNetwork.groupAria': 'Mạng',
  'selectNetwork.all': 'Tất cả mạng',
  'selectNetwork.empty': 'Không có mạng nào khớp với tìm kiếm.',
  'walletTabs.sectionsAria': 'Các mục của ví',
  'walletTabs.tokens': 'Token',
  'walletTabs.approvals': 'Phê duyệt',
  'walletTabs.manage': 'Quản lý tiền mã hóa',
  'walletTabs.history': 'Lịch sử',
  'manage.closeAria': 'Đóng quản lý tiền mã hóa',
  'manage.aria': 'Quản lý tiền mã hóa cho {wallet}',
  'manage.title': 'Quản lý tiền mã hóa',
  'manage.searchPlaceholder': 'Tìm token',
  'manage.searchAria': 'Tìm token theo ký hiệu hoặc tên',
  'manage.empty': 'Ví này chưa có token. Hãy nhận tài sản để quản lý tại đây.',
  'manage.noMatches': 'Không có token nào khớp với tìm kiếm.',
  'manage.tokensAria': 'Token',
  'manage.showTokenAria': 'Hiện {symbol} trên {network}',
  'manage.import': 'Nhập token tùy chỉnh',
  'manage.note': 'Chỉ liệt kê token mà ví này đang giữ. Nhập token bằng địa chỉ hợp đồng để theo dõi tài sản tùy chỉnh. Token được nhập chưa được xác minh và không có dữ liệu giá.',
  'import.closeAria': 'Đóng nhập token',
  'import.aria': 'Nhập token vào {wallet}',
  'import.title': 'Nhập token',
  'import.contract': 'Địa chỉ hợp đồng',
  'import.contractPlaceholder': 'Dán địa chỉ hợp đồng token',
  'import.contractAria': 'Địa chỉ hợp đồng token',
  'import.resolve': 'Tra cứu token',
  'import.resolving': 'Đang tra cứu…',
  'import.unknownToken': 'Token không xác định',
  'import.unnamedToken': 'Token chưa đặt tên',
  'import.decimals': 'Số chữ số thập phân',
  'import.balance': 'Số dư',
  'import.hint': 'Dán địa chỉ hợp đồng token trên {network} rồi tra cứu để xem trước token trước khi thêm.',
  'import.warning': 'Token chưa được xác minh. Hãy tự kiểm tra địa chỉ hợp đồng trước khi thêm. Token được nhập không nằm trong danh sách cho phép đã tuyển chọn và không có dữ liệu giá.',
  'import.add': 'Thêm token',
  'import.error.cap': 'Ví này đã đạt số token được nhập tối đa. Hãy xóa một token trước khi thêm token khác.',

  // Wallet balance and wallet settings
  'walletBalance.tag.unverifiedTitle': 'Token này không nằm trong danh sách cho phép đã tuyển chọn.',
  'walletBalance.testnetPillTitle': 'Ví testnet. Số dư không có giá trị thực.',
  'walletBalance.testnet': 'Testnet',
  'walletBalance.loadingBalances': 'Đang tải số dư...',
  'walletBalance.statusPrefix': 'Trạng thái: {status}',
  'walletBalance.keygen': 'Đang tạo khóa. Số dư sẽ xuất hiện khi hoàn tất.',
  'walletBalance.partial': 'Không thể đọc một số số dư nên dữ liệu có thể chưa đầy đủ.',
  'walletBalance.chainFilterAria': 'Bộ lọc mạng',
  'walletBalance.testnetBanner': 'Testnet — số dư hiển thị ở đây thuộc testnet và không có giá trị thực. Tắt Chế độ nhà phát triển trong menu để dùng mainnet.',
  'walletSettings.addressAria': 'Địa chỉ ví',
  'walletSettings.detailsAria': 'Chi tiết ví',
  'walletSettings.address': 'Địa chỉ',
  'walletSettings.details': 'Chi tiết',
  'walletSettings.addressPending': 'Địa chỉ đang chờ. Địa chỉ sẽ xuất hiện khi khóa của ví hoàn tất.',
  'walletSettings.name': 'Tên',
  'walletSettings.balance': 'Số dư',
  'walletSettings.status': 'Trạng thái',
  'walletSettings.curve': 'Đường cong',
  'walletSettings.publicKey': 'Khóa công khai',
  'walletSettings.shortAddress': 'Địa chỉ rút gọn',
  'walletSettings.pending': 'Đang chờ',
  'walletSettings.unknownChain': 'Mạng không xác định',

  // Welcome / language choice (first-visit screen)
  'welcome.eyebrow': 'Chào mừng đến với',
  'welcome.tagline': 'Bảo vệ điều bạn trân quý',
  'welcome.title': 'Chọn ngôn ngữ của bạn',
  'welcome.cta': 'Bắt đầu',
  'welcome.legalTitle': 'Thông báo pháp lý',
  'welcome.legalText': 'Hỗ trợ ngôn ngữ được cung cấp như một phần trong nỗ lực của chúng tôi nhằm nâng cao khả năng tiếp cận cho người dùng trên toàn thế giới. Hỗ trợ này chỉ được cung cấp tại các khu vực mà pháp luật cho phép việc sử dụng đó và chỉ nhằm tạo thuận tiện cho những người dùng muốn hoặc cần sử dụng ngôn ngữ này.',
  'welcome.legalAgreement': 'Bằng cách nhấp vào nút "Tôi đồng ý" bên dưới, bạn xác nhận rằng việc sử dụng ngôn ngữ này tuân thủ các luật và quy định địa phương.',
  'welcome.agree': 'Tôi đồng ý',

  'start.label': 'Ví',
  'start.points.badge': 'Ứng dụng công nghệ MPC',
  'start.points.lead': 'Không cần khóa riêng tư hay cụm từ khôi phục dễ lộ.',
  'start.points.shares':
    'Ví của bạn được bảo vệ bằng các yếu tố bảo mật (key shard) phân bổ giữa Google, bản sao lưu của bạn và tính năng xác thực trên thiết bị.',
  'start.points.twoRequired': 'Để chuyển tài sản, cần có ít nhất hai yếu tố bảo mật.',
  'start.points.neverWhole':
    'Các yếu tố bảo mật bảo vệ tài sản của bạn không bao giờ được kết hợp hay lưu cùng một nơi.',
  'start.points.rules':
    'Dù một yếu tố bảo mật gặp sự cố, các quy tắc bảo mật bổ sung vẫn bảo vệ tài sản của bạn.',
  'start.points.more': 'Đọc thêm',
  'start.points.less': 'Thu gọn',
  'start.title': 'Tạo ví ẩn danh',
  'start.desc': 'Bảo vệ ví phi tập trung bằng tính năng xác thực trên thiết bị của bạn.',
  'start.hint': 'Khuôn mặt · Vân tay · Khóa truy cập',
  'start.signin.prompt': 'Bạn đã có tài khoản?',
  'start.signin.link': 'Đăng nhập',
  'start.unsupported': 'Trình duyệt này không thể tạo khóa truy cập. Thay vào đó, hãy đăng nhập bằng tài khoản bên thứ ba.',

  // Maintenance screen. Shown when the host answers /v1 with 503.
  'maintenance.title': 'Ví đang được bảo trì',
  'maintenance.body': 'Chúng tôi đang thực hiện bảo trì theo kế hoạch. Bạn có thể sử dụng lại ví sau khi quá trình bảo trì hoàn tất.',
  'maintenance.assurance': 'Tiền và khóa của bạn vẫn an toàn. Việc bị đăng xuất không làm thay đổi tài sản bạn đang nắm giữ.',
  'maintenance.retry': 'Kiểm tra lại',
  'start.secondAccount.title': 'Thiết bị này đã có phương thức xác thực được đăng ký và liên kết với một tài khoản',
  'start.secondAccount.body': 'Bạn có muốn thêm tài khoản khác không?',
  'start.secondAccount.signIn': 'Đến trang đăng nhập',
  'start.secondAccount.create': 'Thêm tài khoản khác',
};
